/**
 * Auth plugin for Vue 3.
 * Installs auth state, services, permissions, and route guard.
 *
 * Usage in main.js:
 *   import { authPlugin } from '@/auth'
 *   app.use(authPlugin, { router })
 */
import { authState, authMutations } from './authState'
import { authService } from './authService'
import { hasPermission, hasAnyPermission, hasAllPermissions, hasAdminAccess, permissionDirective } from './permissions'
import { createAuthGuard } from './authGuard'
// import { sessionMonitor } from './sessionMonitor'

export const authPlugin = {
  install(app, { router }) {
    // Make auth available globally
    app.provide('authState', authState)
    app.provide('authMutations', authMutations)
    app.provide('authService', authService)

    // Global properties for Options API access
    app.config.globalProperties.$auth = authState
    app.config.globalProperties.$can = hasPermission
    app.config.globalProperties.$canAll = hasAllPermissions
    app.config.globalProperties.$canAny = hasAnyPermission
    app.config.globalProperties.$hasAdminAccess = hasAdminAccess

    // Permission directive
    app.directive('permission', permissionDirective)

    // Route guard
    createAuthGuard(router)

    // Initialize auth state
    initializeAuth()
  }
}

async function initializeAuth() {
  authMutations.setLoading(true)
  try {
    const status = await authService.getAuthStatus()
    if (status) {
      authMutations.setUser(status)
      if (status.activeTenant) {
        authMutations.setActiveTenant(status.activeTenant)
        authMutations.setPermissions(status.permissions)
        // sessionMonitor.start() is triggered by App.vue's auth state watcher
      }
    }
  } catch (e) {
    // Not authenticated — that's fine
    authMutations.reset()
  } finally {
    authMutations.setLoading(false)
  }
}

// Re-export everything for direct imports
export { authState, authMutations } from './authState'
export { authService } from './authService'
export { hasPermission, hasAnyPermission, hasAllPermissions, hasAdminAccess, usePermissions } from './permissions'
export { sessionMonitor } from './sessionMonitor'
